import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { getCookie } from '../Utils/Cookies';
import { getUser, getUserId, getCustomerId, getUserName, getUserEmailAddress, getFirstName, getLastName, removeAllCookies } from '../Utils/UserInfo';
import { TestAuthorization, GetCredentials, ClearCredentials } from '../services/AccountServices';
import useHttp from '../Network/Use-http/UseHttp';
import { config } from '../Utils/environment';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isAuthLoading, setIsAuthLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [callApiLoading, callApiError, CallApi] = useHttp();
    
    // Use ref to store CallApi function to prevent infinite loop
    // CallApi changes on every render, but we need stable reference for validateToken
    const callApiRef = useRef(CallApi);
    useEffect(() => {
        callApiRef.current = CallApi;
    }, [CallApi]);

    // Handle logout
    const handleLogout = useCallback(() => {
        removeAllCookies();
        setIsAuthenticated(false);
        setUser(null);
        // Dispatch logout event for other components
        window.dispatchEvent(new Event('userLoggedOut'));
    }, []);

    // Validate token by calling backend
    // skipLoadingState: if true, don't set loading state (for background validation after optimistic update)
    const validateToken = useCallback((skipLoadingState = false) => {
        const token = getCookie('persistence-key');
        
        // If no token exists, user is not authenticated
        if (!token) {
            setIsAuthenticated(false);
            setIsAuthLoading(false);
            setUser(null);
            return;
        }

        // Only set loading state if not skipping (prevents button blink during background validation)
        if (!skipLoadingState) {
            setIsAuthLoading(true);
        }

        // Call backend to validate token - use ref to avoid dependency on CallApi
        TestAuthorization(callApiRef.current, (data) => {
            try {
                const response = data;
                if (response && response.status && response.data) {
                    // Token is valid - set authenticated state
                    const userData = getUser();
                    setIsAuthenticated(true);
                    setUser({
                        userId: getUserId(),
                        customerId: getCustomerId(),
                        userName: getUserName(),
                        emailAddress: getUserEmailAddress(),
                        firstName: getFirstName(),
                        lastName: getLastName(),
                        ...(userData && typeof userData === 'object' ? userData : {})
                    });
                } else {
                    // Token validation failed
                    handleLogout();
                }
            } catch (error) {
                console.error('Token validation error:', error);
                handleLogout();
            } finally {
                setIsAuthLoading(false);
            }
        }, (error) => {
            // API call failed - token is invalid or expired
            console.error('Token validation failed:', error);
            handleLogout();
            setIsAuthLoading(false);
        });
    }, [handleLogout]); // Removed CallApi from dependencies - using ref instead

    // Handle login success
    const handleLogin = useCallback(() => {
        // Just re-validate token after login
        // Don't dispatch event here - LoginModal already dispatches 'userLoggedIn' event
        validateToken();
    }, [validateToken]);

    // Validate token on mount and when cookies change
    useEffect(() => {
        setIsAuthLoading(true);
        validateToken();
    }, [validateToken]);

    // Listen for login/logout events from other components
    useEffect(() => {
        const handleUserLogin = () => {
            // Optimistically update auth state immediately (cookies are already set)
            // This prevents button from showing "Login" during validation
            const token = getCookie('persistence-key');
            if (token) {
                // Immediately update state from cookies (optimistic update)
                // Explicitly set loading to false to ensure button shows immediately
                setIsAuthLoading(false);
                setIsAuthenticated(true);
                setUser({
                    userId: getUserId(),
                    customerId: getCustomerId(),
                    userName: getUserName(),
                    emailAddress: getUserEmailAddress(),
                    firstName: getFirstName(),
                    lastName: getLastName(),
                    ...(getUser() && typeof getUser() === 'object' ? getUser() : {})
                });
            }
            
            // Then validate in background (will correct if token is invalid)
            // Pass skipLoadingState=true to prevent button blink during validation
            // Small delay to ensure cookies are fully set
            setTimeout(() => {
                validateToken(true); // Skip loading state to prevent UI flicker
            }, 100);
        };

        const handleUserLogout = () => {
            handleLogout();
        };

        window.addEventListener('userLoggedIn', handleUserLogin);
        window.addEventListener('userLoggedOut', handleUserLogout);

        return () => {
            window.removeEventListener('userLoggedIn', handleUserLogin);
            window.removeEventListener('userLoggedOut', handleUserLogout);
        };
    }, [validateToken, handleLogout]);

    //#region RedirectToPage - redirect to .NET page with pre-authentication

    /** Redirects to the .NET page with pre-authentication.
     * @param {string} redirectUrl - The URL to redirect to after credentials are retrieved.
     */
    const RedirectToPage = (redirectUrl) => {
        try {
            // Get credentials from server (will try sessionStorage backup if server fails)
            GetCredentials(callApiRef.current,
                // use curried function to pass redirectUrl to getCredentialsResponse
                (respData) => getCredentialsResponse(respData, redirectUrl),
                // Pass redirectUrl to error handler for fallback redirect
                (error) => getCredentialsError(error, redirectUrl)
            );
        } catch (error) {
            console.error('Error in RedirectToPage:', error);
            // Fallback to direct redirect if something goes wrong
            getCredentialsError(error, redirectUrl);
        }
    };
    /** Handles successful response from GetCredentials.
     * @param {{status: boolean, data: {userName: string, password: string}}} responseData - The response data from the GetCredentials call.
     * @param {string} redirectUrl - The URL to redirect to after credentials are retrieved.
     */
    const getCredentialsResponse = (responseData, redirectUrl) => {
        const response = responseData;
        try {
            if (response && response.status && response.data) {
                const { userName, password } = response.data;
                try {
                    // Create a form to POST to the .NET login endpoint
                    const form = document.createElement('form');
                    form.method = 'POST';
                    form.action = `${config.ATHOS_DOT_NET_URL}/Account/Logon?returnUrl=${encodeURIComponent(redirectUrl)}`;

                    form.style.display = 'none';

                    // Add the login credentials as form fields
                    const usernameField = document.createElement('input');
                    usernameField.type = 'hidden';
                    usernameField.name = 'UserName';
                    usernameField.value = userName;
                    form.appendChild(usernameField);

                    const passwordField = document.createElement('input');
                    passwordField.type = 'hidden';
                    passwordField.name = 'Password';
                    passwordField.value = password;
                    form.appendChild(passwordField);

                    const rememberMeField = document.createElement('input');
                    rememberMeField.type = 'hidden';
                    rememberMeField.name = 'RememberMe';
                    rememberMeField.value = 'false';
                    form.appendChild(rememberMeField);

                    // Submit the form
                    document.body.appendChild(form);
                    form.submit();

                    // Clean up the form after submission
                    setTimeout(() => {
                        if (document.body.contains(form)) {
                            document.body.removeChild(form);
                        }
                        // Clear credentials from server after successful redirect
                        ClearCredentials(callApiRef.current, clearCredentialsResponse, clearCredentialsError);
                    }, 1000);
                } catch (formError) {
                    console.error('Form POST failed:', formError);
                }
            } else {
                console.error('Failed to retrieve credentials:', response?.message);
                // Fallback to direct redirect without pre-authentication
                getCredentialsError('Failed to retrieve credentials', redirectUrl);
            }
        } catch (error) {
            console.error('Error retrieving credentials:', error);
            // Fallback to direct redirect without pre-authentication
            getCredentialsError(error, redirectUrl);
        }
    };

    const clearCredentialsResponse = (responseData) => {
        const response = responseData;
        if (!(response && response.status && response.data)) {
            console.error('Failed to clear credentials:', response?.message);
        }
    };
    const clearCredentialsError = (error) => {
        console.error('Error clearing credentials:', error);
    };

    /** Handles error when credentials cannot be retrieved.
     * Falls back to direct redirect to .NET login page where user can login manually.
     */
    const getCredentialsError = (error, redirectUrl) => {
        console.error('Error getting credentials:', error);
        // Fallback: Redirect to .NET login page without pre-authentication
        // User will need to login manually, but they'll be redirected to the intended page after login
        try {
            window.location.href = `${config.ATHOS_DOT_NET_URL}/Account/Logon?returnUrl=${encodeURIComponent(redirectUrl || '/Dashboard')}`;
        } catch (redirectError) {
            console.error('Error redirecting to .NET site:', redirectError);
            // Last resort: redirect to dashboard
            window.location.href = `${config.ATHOS_DOT_NET_URL}/Dashboard`;
        }
    };
    //#endregion

    const value = {
        isAuthenticated,
        isAuthLoading,
        user,
        login: handleLogin,
        logout: handleLogout,
        refreshAuth: validateToken,
        RedirectToPage,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
